import React, { useCallback, useEffect, useState } from 'react';
/**
 * `ReloadHandler` guards against accidental navigation/refresh during the
 * journey.
 *
 * Behaviour:
 * - Registers `beforeunload` to trigger the browser's native leave-page prompt.
 * - Intercepts F5 (keyCode 116) to show a custom confirmation modal.
 * - Intercepts browser back navigation (`popstate`) to warn and redirect.
 * - Uses `setCountryCode` to generate the reload navigation URL.
 */
import { useNavigate } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { setCountryCode } from './HelperMethods';
import { useAppSelector } from '../../app-state/Hooks';

type ReloadHandlerProps = {
  reloadPath: string;
};

const ReloadHandler = ({ reloadPath }: ReloadHandlerProps): JSX.Element => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const title = 'Confirm Reload';
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);

  useEffect(() => {
    //before unload catches the refresh button
    window.addEventListener('beforeunload', beforeUnloadHandler);
    //keydown catches the f5 refresh
    window.addEventListener('keydown', keydownHandler);
    //popstate catches the back button click
    window.onpopstate = (e: PopStateEvent) => popstateHandler(e);

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      window.removeEventListener('keydown', keydownHandler);
    };
  }, []);

  const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
    let showAlert = true;
    if (e && e.target && e.target instanceof Document) {
      const redirectToPA = window.localStorage.getItem('redirectToPA');
      showAlert =
        e.target.documentURI.includes('summary') &&
        redirectToPA &&
        redirectToPA === 'true'
          ? false
          : true;

      if (redirectToPA) {
        window.localStorage.removeItem('redirectToPA');
      }

      const activeElement = (e.target as Document).activeElement;

      if (
        activeElement?.tagName.toLowerCase() === 'a' &&
        (activeElement
          .getAttribute('href')
          ?.toLowerCase()
          .startsWith('mailto') ||
          activeElement.getAttribute('href')?.toLowerCase().startsWith('tel'))
      )
        showAlert = false;
    }
    //this will show a browser leaving page popup, this is not customisable and can
    if (showAlert) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  const keydownHandler = useCallback((e: KeyboardEvent) => {
    //here we can display a custom modal to confirm if the page is to refresh or not
    if (e.keyCode === 116) {
      e.preventDefault();
      setShowDialog(true);
    }
  }, []);

  const popstateHandler = (e: PopStateEvent) => {
    e.preventDefault();
    window.alert(
      'You have clicked the browser back button, if you have not completed the purchase you will have to start the process again'
    );
    onReload();
  };

  const onReload = () => {
    const path = setCountryCode(reloadPath, countryCode);
    navigate(path);
  };

  const onCloseConfirmation = (): void => {
    setShowDialog(false);
  };

  return (
    <div>
      <Modal isOpen={showDialog}>
        {title && (
          <ModalHeader toggle={onCloseConfirmation}>{title}</ModalHeader>
        )}
        <ModalBody>
          Are you sure you want to continue? Content/data will not be saved.
        </ModalBody>
        <ModalFooter>
          <button id='btn-reload' onClick={onReload}>
            Reload
          </button>
          <button name='btn-close' onClick={onCloseConfirmation}>
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ReloadHandler;
